import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getFoodTrucks, createFoodTruck, deleteFoodTruck, getKPIs, getExportAdherentsUrl, bulkDeleteFoodTrucks } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { BulkSelectBar, useSelection } from "../components/ui/bulk-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plus,
  Search,
  Truck,
  Mail,
  Phone,
  MapPin,
  Euro,
  Users,
  Trash2,
  Eye,
  ChefHat,
  TrendingUp,
  Target,
  AlertCircle,
  Filter,
  X,
  Building2,
  CreditCard,
  AlertTriangle,
  Download,
} from "lucide-react";

export default function FoodTrucksPage() {
  const [foodTrucks, setFoodTrucks] = useState([]);
  const [filteredTrucks, setFilteredTrucks] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Selection for bulk operations
  const selection = useSelection(filteredTrucks);
  
  // Filtres
  const [filters, setFilters] = useState({
    statut: "tous",
    membreBureau: "tous",
    paiement: "tous",
    specialite: "tous",
  });
  
  const [formData, setFormData] = useState({
    nom_food_truck: "",
    specialite: "",
    nom_contact: "",
    email: "",
    telephone: "",
    adresse: "",
    membre_bureau: false,
    cotisation_annuelle: 150,
  });
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  // Get unique specialties from data
  const allSpecialites = [...new Set(foodTrucks.flatMap(ft => 
    ft.specialites || (ft.specialite ? [ft.specialite] : [])
  ))].sort();

  useEffect(() => {
    loadFoodTrucks();
  }, []);

  useEffect(() => {
    let result = [...foodTrucks];
    
    // Filtre recherche texte
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(ft =>
        ft.nom_food_truck?.toLowerCase().includes(searchLower) ||
        ft.nom_contact?.toLowerCase().includes(searchLower) ||
        ft.email?.toLowerCase().includes(searchLower) ||
        ft.telephone?.includes(search) ||
        (ft.specialites || []).some(s => s.toLowerCase().includes(searchLower)) ||
        ft.specialite?.toLowerCase().includes(searchLower)
      );
    }
    
    // Filtre statut
    if (filters.statut !== "tous") {
      result = result.filter(ft => ft.statut === filters.statut);
    }
    
    // Filtre membre bureau
    if (filters.membreBureau !== "tous") {
      result = result.filter(ft => 
        filters.membreBureau === "oui" ? ft.membre_bureau : !ft.membre_bureau
      );
    }
    
    // Filtre paiement
    if (filters.paiement !== "tous") {
      if (filters.paiement === "a_jour") {
        result = result.filter(ft => (ft.solde_du || 0) === 0);
      } else if (filters.paiement === "impaye") {
        result = result.filter(ft => (ft.solde_du || 0) > 0);
      } else if (filters.paiement === "bloque") {
        result = result.filter(ft => (ft.relances_impaye || 0) >= 3);
      }
    }
    
    // Filtre spécialité
    if (filters.specialite !== "tous") {
      result = result.filter(ft => 
        (ft.specialites || []).includes(filters.specialite) ||
        ft.specialite === filters.specialite
      );
    }
    
    setFilteredTrucks(result);
  }, [search, foodTrucks, filters]);

  const resetFilters = () => {
    setFilters({
      statut: "tous",
      membreBureau: "tous",
      paiement: "tous",
      specialite: "tous",
    });
    setSearch("");
  };

  const activeFiltersCount = Object.values(filters).filter(v => v !== "tous").length + (search ? 1 : 0);

  const loadFoodTrucks = async () => {
    try {
      const [ftRes, kpisRes] = await Promise.all([getFoodTrucks(), getKPIs()]);
      setFoodTrucks(ftRes.data);
      setFilteredTrucks(ftRes.data);
      setKpis(kpisRes.data);
    } catch (error) {
      toast.error("Erreur de chargement des food trucks");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createFoodTruck(formData);
      toast.success("Food truck créé avec succès");
      setIsDialogOpen(false);
      setFormData({
        nom_food_truck: "",
        specialite: "",
        nom_contact: "",
        email: "",
        telephone: "",
        adresse: "",
        membre_bureau: false,
        cotisation_annuelle: 150,
      });
      loadFoodTrucks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de création");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteFoodTruck(deleteTarget);
      toast.success("Food truck supprimé");
      setDeleteTarget(null);
      loadFoodTrucks();
    } catch (error) {
      toast.error("Erreur de suppression");
    }
  };

  const handleBulkDelete = async () => {
    const ids = selection.getSelectedIds();
    if (ids.length === 0) return;
    
    setIsDeleting(true);
    try {
      const res = await bulkDeleteFoodTrucks(ids);
      toast.success(res.data.message);
      selection.deselectAll();
      loadFoodTrucks();
    } catch (error) {
      toast.error("Erreur de suppression en lot");
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0" data-testid="food-trucks-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-black text-primary">
              Adhérents
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              {foodTrucks.length} food trucks
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {isAdmin() && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const token = localStorage.getItem("token");
                    window.open(`${getExportAdherentsUrl()}?token=${token}`, "_blank");
                  }}
                  className="border-[#E5E0DC] text-xs sm:text-sm"
                  data-testid="export-adherents-btn"
                >
                  <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  <span className="hidden sm:inline">Export </span>Excel
                </Button>
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      size="sm"
                      className="bg-primary hover:bg-primary-hover text-white shadow-md text-xs sm:text-sm"
                      data-testid="add-food-truck-btn"
                    >
                      <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                      <span className="hidden sm:inline">Nouveau food truck</span>
                      <span className="sm:hidden">Ajouter</span>
                    </Button>
                  </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto mx-2 sm:mx-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl sm:text-2xl text-primary">
                  Ajouter un food truck
                </DialogTitle>
                <DialogDescription className="text-sm">
                  Renseignez les informations du nouveau membre
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="sm:col-span-2">
                    <Label className="text-secondary font-semibold text-sm">Nom du food truck *</Label>
                    <Input
                      value={formData.nom_food_truck}
                      onChange={(e) => setFormData({ ...formData, nom_food_truck: e.target.value })}
                      className="mt-1 border-[#E5E0DC]"
                      placeholder="Ex: La Cantine Mobile"
                      data-testid="input-nom-food-truck"
                      required
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-secondary font-semibold text-sm">Spécialité *</Label>
                    <Input
                      value={formData.specialite}
                      onChange={(e) => setFormData({ ...formData, specialite: e.target.value })}
                      className="mt-1 border-[#E5E0DC]"
                      placeholder="Ex: Cuisine française"
                      data-testid="input-specialite"
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-secondary font-semibold">Nom du contact *</Label>
                    <Input
                      value={formData.nom_contact}
                      onChange={(e) => setFormData({ ...formData, nom_contact: e.target.value })}
                      className="mt-1 border-[#E5E0DC]"
                      placeholder="Jean Dupont"
                      data-testid="input-nom-contact"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="mt-1 border-[#E5E0DC]"
                      placeholder="email@example.com"
                      data-testid="input-email"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Téléphone *</Label>
                    <Input
                      value={formData.telephone}
                      onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                      className="mt-1 border-[#E5E0DC]"
                      placeholder="06 XX XX XX XX"
                      data-testid="input-telephone"
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-secondary font-semibold">Adresse</Label>
                    <Input
                      value={formData.adresse}
                      onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                      className="mt-1 border-[#E5E0DC]"
                      placeholder="Adresse complète"
                      data-testid="input-adresse"
                    />
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Cotisation (€)</Label>
                    <Input
                      type="number"
                      inputMode="decimal"
                      value={formData.cotisation_annuelle === 0 ? "" : formData.cotisation_annuelle}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({ ...formData, cotisation_annuelle: val === "" ? 0 : parseFloat(val) || 0 });
                      }}
                      className="mt-1 border-[#E5E0DC]"
                      data-testid="input-cotisation"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="flex items-center gap-3 pt-6">
                    <Switch
                      checked={formData.membre_bureau}
                      onCheckedChange={(checked) => setFormData({ ...formData, membre_bureau: checked, exempt_cotisation: checked })}
                      data-testid="switch-membre-bureau"
                    />
                    <Label className="text-secondary font-semibold">Membre du bureau (exempté de cotisation)</Label>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="submit-food-truck-btn">
                    Créer
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
              </>
            )}
          </div>
        </div>
      </div>

      {/* KPIs */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white border-[#E5E0DC]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Truck className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total</p>
                  <p className="text-xl font-heading font-bold text-secondary">{kpis.total_food_trucks}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E5E0DC]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-status-success/10">
                  <TrendingUp className="w-5 h-5 text-status-success" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Actifs</p>
                  <p className="text-xl font-heading font-bold text-status-success">{kpis.food_trucks_actifs}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E5E0DC]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-highlight/10">
                  <Users className="w-5 h-5 text-highlight" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Bureau</p>
                  <p className="text-xl font-heading font-bold text-highlight">{kpis.food_trucks_bureau}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E5E0DC]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-accent/10">
                  <Target className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Taux à jour</p>
                  <p className="text-xl font-heading font-bold text-accent">{kpis.taux_a_jour}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search & Filters */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Rechercher un food truck..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 border-[#E5E0DC] bg-white"
              data-testid="search-food-trucks"
            />
          </div>
          <Button
            variant={showFilters ? "default" : "outline"}
            onClick={() => setShowFilters(!showFilters)}
            className={showFilters ? "bg-primary" : ""}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filtres
            {activeFiltersCount > 0 && (
              <Badge className="ml-2 bg-highlight text-white">{activeFiltersCount}</Badge>
            )}
          </Button>
          {activeFiltersCount > 0 && (
            <Button variant="ghost" onClick={resetFilters} className="text-muted-foreground" data-testid="reset-filters-btn">
              <X className="w-4 h-4 mr-2" />
              Réinitialiser
            </Button>
          )}
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <Card className="bg-white border-[#E5E0DC] p-4" data-testid="filters-panel">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Statut Filter */}
              <div className="space-y-2">
                <Label className="text-secondary font-semibold flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" /> Statut
                </Label>
                <Select
                  value={filters.statut}
                  onValueChange={(value) => setFilters({ ...filters, statut: value })}
                >
                  <SelectTrigger className="border-[#E5E0DC]" data-testid="filter-statut">
                    <SelectValue placeholder="Tous les statuts" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tous">Tous les statuts</SelectItem>
                    <SelectItem value="actif">Actif</SelectItem>
                    <SelectItem value="inactif">Inactif</SelectItem>
                    <SelectItem value="en_attente_validation">En attente</SelectItem>
                    <SelectItem value="bloque_documents">Bloqué (documents)</SelectItem>
                    <SelectItem value="bloque_paiement">Bloqué (paiement)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Membre Bureau Filter */}
              <div className="space-y-2">
                <Label className="text-secondary font-semibold flex items-center gap-2">
                  <Building2 className="w-4 h-4" /> Membre bureau
                </Label>
                <Select
                  value={filters.membreBureau}
                  onValueChange={(value) => setFilters({ ...filters, membreBureau: value })}
                >
                  <SelectTrigger className="border-[#E5E0DC]" data-testid="filter-membre-bureau">
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tous">Tous</SelectItem>
                    <SelectItem value="oui">Membres du bureau</SelectItem>
                    <SelectItem value="non">Membres standards</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Paiement Filter */}
              <div className="space-y-2">
                <Label className="text-secondary font-semibold flex items-center gap-2">
                  <CreditCard className="w-4 h-4" /> Paiement
                </Label>
                <Select
                  value={filters.paiement}
                  onValueChange={(value) => setFilters({ ...filters, paiement: value })}
                >
                  <SelectTrigger className="border-[#E5E0DC]" data-testid="filter-paiement">
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tous">Tous</SelectItem>
                    <SelectItem value="a_jour">À jour</SelectItem>
                    <SelectItem value="impaye">Impayé</SelectItem>
                    <SelectItem value="bloque">Bloqué (3+ relances)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Spécialité Filter */}
              <div className="space-y-2">
                <Label className="text-secondary font-semibold flex items-center gap-2">
                  <ChefHat className="w-4 h-4" /> Spécialité
                </Label>
                <Select
                  value={filters.specialite}
                  onValueChange={(value) => setFilters({ ...filters, specialite: value })}
                >
                  <SelectTrigger className="border-[#E5E0DC]" data-testid="filter-specialite">
                    <SelectValue placeholder="Toutes" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="tous">Toutes les spécialités</SelectItem>
                    {allSpecialites.map((spec) => (
                      <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>
        )}

        {/* Active filters summary */}
        {activeFiltersCount > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="active-filters-summary">
            {search && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Recherche: "{search}"
                <X className="w-3 h-3 cursor-pointer" onClick={() => setSearch("")} />
              </Badge>
            )}
            {filters.statut !== "tous" && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Statut: {filters.statut.replace(/_/g, " ")}
                <X className="w-3 h-3 cursor-pointer" onClick={() => setFilters({ ...filters, statut: "tous" })} />
              </Badge>
            )}
            {filters.membreBureau !== "tous" && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Bureau: {filters.membreBureau === "oui" ? "Oui" : "Non"}
                <X className="w-3 h-3 cursor-pointer" onClick={() => setFilters({ ...filters, membreBureau: "tous" })} />
              </Badge>
            )}
            {filters.paiement !== "tous" && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Paiement: {filters.paiement.replace(/_/g, " ")}
                <X className="w-3 h-3 cursor-pointer" onClick={() => setFilters({ ...filters, paiement: "tous" })} />
              </Badge>
            )}
            {filters.specialite !== "tous" && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Spécialité: {filters.specialite}
                <X className="w-3 h-3 cursor-pointer" onClick={() => setFilters({ ...filters, specialite: "tous" })} />
              </Badge>
            )}
            <span className="text-sm text-muted-foreground ml-2">
              {filteredTrucks.length} résultat(s)
            </span>
          </div>
        )}
      </div>

      {/* Grid */}
      {filteredTrucks.length > 0 ? (
        <>
          {isAdmin() && (
            <BulkSelectBar
              selectedCount={selection.selectedCount}
              totalCount={filteredTrucks.length}
              onSelectAll={selection.selectAll}
              onDeselectAll={selection.deselectAll}
              onDelete={handleBulkDelete}
              isDeleting={isDeleting}
              itemName="food truck"
            />
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTrucks.map((ft) => (
              <Card
                key={ft.id}
                className={`bg-white border-[#E5E0DC] hover:shadow-md transition-shadow card-hover group ${selection.isSelected(ft.id) ? "ring-2 ring-primary" : ""}`}
                data-testid={`food-truck-card-${ft.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {isAdmin() && (
                        <Checkbox
                          checked={selection.isSelected(ft.id)}
                          onCheckedChange={() => selection.toggleSelect(ft.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                        />
                      )}
                      <div 
                        className="flex items-center gap-3 cursor-pointer"
                        onClick={() => navigate(`/food-trucks/${ft.id}`)}
                      >
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                          <Truck className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="font-heading text-lg text-secondary group-hover:text-primary transition-colors">
                            {ft.nom_food_truck}
                          </CardTitle>
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <ChefHat className="w-3 h-3" /> {ft.specialite}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      <Badge
                        variant={ft.statut === "actif" ? "default" : "secondary"}
                        className={ft.statut === "actif" ? "bg-status-success" : ""}
                      >
                        {ft.statut}
                      </Badge>
                      {ft.membre_bureau && (
                        <Badge variant="outline" className="border-highlight text-highlight">
                          <Users className="w-3 h-3 mr-1" /> Bureau
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3" onClick={() => navigate(`/food-trucks/${ft.id}`)}>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                    <Mail className="w-4 h-4" />
                    <span className="truncate">{ft.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                    <Phone className="w-4 h-4" />
                    <span>{ft.telephone}</span>
                  </div>
                  {ft.adresse && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                      <MapPin className="w-4 h-4" />
                      <span className="truncate">{ft.adresse}</span>
                    </div>
                  )}
                  <div className="pt-3 border-t border-[#E5E0DC] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Euro className="w-4 h-4 text-muted-foreground" />
                      <span className={`font-semibold ${ft.solde_du > 0 ? "text-status-warning" : "text-status-success"}`}>
                        {ft.solde_du > 0 ? `${ft.solde_du.toFixed(2)} € dû` : "À jour"}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/food-trucks/${ft.id}`);
                        }}
                        className="text-primary hover:text-primary-hover"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {isAdmin() && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(ft.id);
                          }}
                          className="text-status-error hover:text-status-error/80"
                          data-testid={`delete-food-truck-${ft.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      ) : (
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-12 text-center">
            <Truck className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading text-xl text-secondary mb-2">Aucun food truck trouvé</h3>
            <p className="text-muted-foreground">
              {search ? "Aucun résultat pour cette recherche" : "Ajoutez votre premier food truck"}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le food truck et toutes ses données associées seront supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-status-error hover:bg-status-error/90"
              data-testid="confirm-delete-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
